import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  Collapse,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getClients } from '../../../../redux/actions/client';
import {
  getLeadItems,
  getLeadUsers,
  updateLead,
  getLeads,
} from '../../../../redux/actions/lead';
import {
  SOURCE_OPTIONS,
  STATE_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from '../../constants';
import { formatFormCurrency } from '../../../../utils/utils';

const STATE_ORDER = ['new', 'assigned', 'negotiation', 'visit', 'close', 'completed'];

function buildInitialFormValues(lead) {
  if (!lead) return null;
  const d = lead.data || lead;
  return {
    clientId: d.clientId ?? '',
    holdingId: d.holdingId ?? '',
    state: d.state ?? 'new',
    useCompanyData: d.useCompanyData ?? false,
    source: d.new?.source ?? '',
    assignedUserEmail: d.assigned?.userEmail ?? '',
    contactMethod: d.negotiation?.contactMethod ?? '',
    visitDatetime: d.visit?.datetime ?? '',
    visitIsArrived: d.visit?.isArrived ?? false,
    visitPrice: d.visit?.price != null && d.visit?.price !== '' ? formatFormCurrency(String(d.visit.price)) : '',
    visitIva: d.visit?.iva ?? 'Mas IVA',
    visitPaymentMethod: d.visit?.paymentMethod ?? '',
    visitObservation: d.visit?.observation ?? '',
    closeDate: d.close?.date ?? '',
    closeComment: d.close?.comment ?? '',
    completedDate: d.completed?.date ?? '',
    completedPrice: d.completed?.price != null && d.completed?.price !== '' ? formatFormCurrency(String(d.completed.price)) : '',
    completedPaymentMethod: d.completed?.paymentMethod ?? '',
    completedComment: d.completed?.comment ?? '',
  };
}

function buildFullDataPayload(values, initialLead) {
  const d = initialLead?.data || initialLead || {};
  const now = new Date().toISOString();
  const order = 0;

  const payload = {
    clientId: Number(values.clientId),
    holdingId: String(values.holdingId).trim(),
    state: values.state,
    useCompanyData: Boolean(values.useCompanyData),
    new: {
      source: values.source || 'otros',
      order,
      updatedAt: now,
      createdAt: d.new?.createdAt || now,
    },
    assigned: null,
    negotiation: null,
    visit: null,
    close: null,
    completed: null,
  };

  const stateIdx = STATE_ORDER.indexOf(values.state);

  if (stateIdx >= 1 && values.assignedUserEmail) {
    payload.assigned = {
      userEmail: values.assignedUserEmail,
      order,
      updatedAt: now,
    };
  }

  if (stateIdx >= 2) {
    payload.negotiation = {
      contactMethod: values.contactMethod || 'otros',
      order,
      updatedAt: now,
      quotationId: d.negotiation?.quotationId ?? null,
    };
  }

  if (stateIdx >= 3) {
    payload.visit = {
      datetime: values.visitDatetime || now,
      isArrived: Boolean(values.visitIsArrived),
      order,
      updatedAt: now,
      offerId: d.visit?.offerId ?? null,
    };
    if (values.visitIsArrived) {
      payload.visit.price = values.visitPrice ? String(values.visitPrice).replace(/\./g, '').replace(/\D/g, '') : '';
      payload.visit.iva = values.visitIva === 'Mas IVA' ? '+ IVA' : values.visitIva;
      payload.visit.paymentMethod = values.visitPaymentMethod || '';
      payload.visit.observation = values.visitObservation || '';
    }
  }

  if (stateIdx >= 4) {
    payload.close = {
      date: values.closeDate || now.slice(0, 10),
      comment: values.closeComment || '',
      order,
      updatedAt: now,
    };
  }

  if (stateIdx >= 5) {
    payload.completed = {
      date: values.completedDate || now.slice(0, 10),
      price: values.completedPrice ? String(values.completedPrice).replace(/\./g, '').replace(/\D/g, '') : '',
      paymentMethod: values.completedPaymentMethod || '',
      comment: values.completedComment || '',
      order,
      updatedAt: now,
    };
  }

  return payload;
}

function LeadEditForm({ leadId, initialLead, onSubmit, onCancel }) {
  const dispatch = useDispatch();
  const clientState = useSelector((state) => state.client.get);
  const holdingState = useSelector((state) => state.lead.items);
  const userState = useSelector((state) => state.lead.users);
  const updateState = useSelector((state) => state.lead.update);

  const [modal, setModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [holdingSearch, setHoldingSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [showHoldingSuggestions, setShowHoldingSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [openSections, setOpenSections] = useState({ base: true, assigned: true });

  const [values, setValues] = useState(() => buildInitialFormValues(initialLead) || {});

  useEffect(() => {
    if (initialLead) {
      const d = initialLead.data || initialLead;
      setValues((prev) => ({ ...prev, ...buildInitialFormValues(initialLead) }));
      if (d.clientId != null)
        setSelectedClient({ id: d.clientId, data: d.clientInfo || {} });
      if (d.holdingId != null)
        setSelectedHolding({ id: d.holdingId, ...(d.holdingInfo || {}) });
    }
  }, [initialLead]);

  useEffect(() => {
    if (clientState?.data) setClients(clientState.data);
  }, [clientState]);

  useEffect(() => {
    if (updateState) {
      setLoading(false);
      if (updateState.status === 'success') {
        setModal(false);
        dispatch(getLeads());
        if (onSubmit) onSubmit();
      } else if (updateState.status === 'danger') {
        setErrorMessage(updateState.message || 'Error al guardar el lead');
      }
    }
  }, [updateState, dispatch, onSubmit]);

  const handleChange = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setValues((prev) => ({ ...prev, [field]: v }));
  };

  const handleClientSearch = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setShowClientSuggestions(true);
    if (value.length > 0) dispatch(getClients(value));
    else setClients([]);
  };

  const handleHoldingSearch = (e) => {
    const value = e.target.value;
    setHoldingSearch(value);
    setShowHoldingSuggestions(true);
    if (value.length > 0) dispatch(getLeadItems(value));
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setValues((prev) => ({ ...prev, clientId: client.id }));
    setShowClientSuggestions(false);
  };

  const handleHoldingSelect = (holding) => {
    if (!holding) return;
    const id = holding.id ?? holding.identifier;
    const h = { id, ...holding };
    setSelectedHolding(h);
    setHoldingSearch('');
    setValues((prev) => ({ ...prev, holdingId: id }));
    setShowHoldingSuggestions(false);
  };

  const handleRemoveClient = () => {
    setSelectedClient(null);
    setValues((prev) => ({ ...prev, clientId: '' }));
  };

  const handleRemoveHolding = () => {
    setSelectedHolding(null);
    setValues((prev) => ({ ...prev, holdingId: '' }));
  };

  const toggle = (operation) => {
    setModal(!modal);
    if (operation === 'submit') {
      if (onSubmit) onSubmit();
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!leadId) {
      setErrorMessage('ID de lead no válido');
      return;
    }
    if (!values.clientId || !values.holdingId) {
      setErrorMessage('Cliente y bien son obligatorios');
      return;
    }
    if (!values.state) {
      setErrorMessage('Estado es obligatorio');
      return;
    }
    const stateIdx = STATE_ORDER.indexOf(values.state);
    if (stateIdx >= 1 && !values.assignedUserEmail) {
      setErrorMessage('Debe asignar un vendedor cuando el estado es Asignado o posterior');
      return;
    }
    setLoading(true);
    const data = buildFullDataPayload(values, initialLead);
    dispatch(updateLead(leadId, { fullData: true, data }));
  };

  useEffect(() => {
    dispatch(getLeadUsers());
  }, [dispatch]);

  const users = userState?.data || [];
  const stateIdx = STATE_ORDER.indexOf(values.state);

  return (
    <Modal size="lg" isOpen={modal} toggle={() => toggle('cancel')}>
      <ModalHeader toggle={() => toggle('cancel')}>
        Editar lead (edición completa)
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {errorMessage && (
            <Alert color="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}

          <div className="mb-3">
            <Button
              type="button"
              color="link"
              size="sm"
              className="p-0"
              onClick={() => setOpenSections((s) => ({ ...s, base: !s.base }))}
            >
              {openSections.base ? '▼' : '▶'} Información base
            </Button>
            <Collapse isOpen={openSections.base}>
              <FormGroup>
                <Label>Cliente</Label>
                <div className="position-relative">
                  {selectedClient ? (
                    <div className="d-flex align-items-center mb-2">
                      <Badge color="primary" className="mr-2 p-2">
                        {selectedClient.data?.companyInfo?.name ||
                          `${selectedClient.data?.firstName || ''} ${selectedClient.data?.lastName || ''}`.trim() ||
                          `ID ${selectedClient.id}`}
                      </Badge>
                      <Button color="link" size="sm" className="p-0" onClick={handleRemoveClient}>
                        <i className="fa fa-times" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        placeholder="Buscar cliente..."
                        value={clientSearch}
                        onChange={handleClientSearch}
                        onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                      />
                      {showClientSuggestions && clients.length > 0 && (
                        <div
                          className="position-absolute w-100 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000 }}
                        >
                          {clients.map((c) => (
                            <div
                              key={c.id}
                              className="px-3 py-2 cursor-pointer hover-bg-light"
                              onClick={() => handleClientSelect(c)}
                            >
                              {c.data?.firstName} {c.data?.lastName} - {c.data?.rut}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Bien</Label>
                <div className="position-relative">
                  {selectedHolding ? (
                    <div className="d-flex align-items-center mb-2">
                      <Badge color="primary" className="mr-2 p-2">
                        {selectedHolding.id ?? '--'} | {selectedHolding.brand ?? '--'} | {selectedHolding.model ?? '--'}
                      </Badge>
                      <Button color="link" size="sm" className="p-0" onClick={handleRemoveHolding}>
                        <i className="fa fa-times" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        placeholder="Buscar bien por número interno..."
                        value={holdingSearch ?? ''}
                        onChange={handleHoldingSearch}
                        onBlur={() => setTimeout(() => setShowHoldingSuggestions(false), 200)}
                      />
                      {showHoldingSuggestions && (() => {
                        const raw = holdingState?.data;
                        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
                        const safeList = list.filter((h) => h != null);
                        if (safeList.length === 0) return null;
                        return (
                          <div
                            className="position-absolute w-100 bg-white border rounded shadow-sm"
                            style={{ zIndex: 1000 }}
                          >
                            {safeList.map((h) => (
                              <div
                                key={h.id ?? h.identifier ?? String(h)}
                                className="px-3 py-2 cursor-pointer"
                                onClick={() => handleHoldingSelect(h)}
                              >
                                {(h.id ?? h.identifier ?? '--')} - {(h.brand ?? '--')} {(h.model ?? '--')}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </FormGroup>
              <FormGroup>
                <Label for="state">Estado</Label>
                <Input
                  type="select"
                  id="state"
                  value={values.state}
                  onChange={handleChange('state')}
                >
                  {STATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label for="source">Fuente (Nuevo)</Label>
                <Input
                  type="select"
                  id="source"
                  value={values.source}
                  onChange={handleChange('source')}
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup check className="pl-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={values.useCompanyData}
                    onChange={handleChange('useCompanyData')}
                  />
                  Usar datos de empresa
                </Label>
              </FormGroup>
            </Collapse>
          </div>

          {/* PDFs de cotización y oferta */}
          {(initialLead?.data?.negotiation?.pdf ||
            (initialLead?.data?.visit?.pdf && initialLead?.data?.visit?.isArrived)) && (
            <div className="mb-3 p-3 border rounded bg-light">
              <h6 className="text-muted small font-weight-bold mb-2">Documentos PDF</h6>
              {initialLead?.data?.negotiation?.pdf && (
                <div className="mb-2">
                  <strong>Cotización:</strong>{' '}
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => window.open(initialLead.data.negotiation.pdf, '_blank')}
                  >
                    <i className="fa fa-download mr-1" />
                    Descargar PDF
                  </Button>
                </div>
              )}
              {initialLead?.data?.visit?.pdf && initialLead?.data?.visit?.isArrived && (
                <div>
                  <strong>Oferta:</strong>{' '}
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => window.open(initialLead.data.visit.pdf, '_blank')}
                  >
                    <i className="fa fa-download mr-1" />
                    Descargar PDF
                  </Button>
                </div>
              )}
            </div>
          )}

          {stateIdx >= 1 && (
            <div className="mb-3">
              <Button
                type="button"
                color="link"
                size="sm"
                className="p-0"
                onClick={() => setOpenSections((s) => ({ ...s, assigned: !s.assigned }))}
              >
                {openSections.assigned ? '▼' : '▶'} Asignado
              </Button>
              <Collapse isOpen={openSections.assigned}>
                <FormGroup>
                  <Label for="assignedUserEmail">Vendedor asignado</Label>
                  <Input
                    type="select"
                    id="assignedUserEmail"
                    value={values.assignedUserEmail}
                    onChange={handleChange('assignedUserEmail')}
                  >
                    <option value="">Seleccione vendedor</option>
                    {users.map((u) => (
                      <option key={u.email} value={u.email}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Collapse>
            </div>
          )}

          {stateIdx >= 2 && (
            <FormGroup>
              <Label>Método de contacto (Negociación)</Label>
              <Input
                type="select"
                value={values.contactMethod}
                onChange={handleChange('contactMethod')}
              >
                {CONTACT_METHOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}

          {stateIdx >= 3 && (
            <>
              <FormGroup>
                <Label>Fecha y hora visita</Label>
                <Input
                  type="datetime-local"
                  value={values.visitDatetime}
                  onChange={handleChange('visitDatetime')}
                />
              </FormGroup>
              <FormGroup
                tag="fieldset"
                className="border rounded p-3 pl-4 bg-light mb-3"
              >
                <Label className="d-flex align-items-center mb-0">
                  <Input
                    type="checkbox"
                    className="mr-2"
                    style={{ width: '18px', height: '18px' }}
                    checked={values.visitIsArrived}
                    onChange={handleChange('visitIsArrived')}
                  />
                  <span className="font-weight-bold">¿El cliente asistió a la visita?</span>
                </Label>
              </FormGroup>
              {values.visitIsArrived && (
                <>
                  <FormGroup>
                    <Label for="visitPrice">Precio de la visita</Label>
                    <Input
                      id="visitPrice"
                      type="text"
                      placeholder="Ej: 1.500.000"
                      value={values.visitPrice ?? ''}
                      onChange={(e) => {
                        const raw = (e && e.target != null) ? e.target.value : '';
                        setValues((p) => ({ ...p, visitPrice: formatFormCurrency(raw) }));
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>IVA</Label>
                    <Input type="select" value={values.visitIva} onChange={handleChange('visitIva')}>
                      <option value="Mas IVA">Más IVA</option>
                      <option value="Con IVA">Con IVA</option>
                      <option value="Exento de IVA">Exento de IVA</option>
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label>Método de pago (visita)</Label>
                    <Input
                      type="select"
                      value={values.visitPaymentMethod}
                      onChange={handleChange('visitPaymentMethod')}
                    >
                      {PAYMENT_METHOD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label>Observación visita</Label>
                    <Input
                      type="textarea"
                      value={values.visitObservation}
                      onChange={handleChange('visitObservation')}
                    />
                  </FormGroup>
                </>
              )}
            </>
          )}

          {stateIdx >= 4 && (
            <>
              <FormGroup>
                <Label>Fecha cierre</Label>
                <Input
                  type="date"
                  value={values.closeDate}
                  onChange={handleChange('closeDate')}
                />
              </FormGroup>
              <FormGroup>
                <Label>Comentario cierre</Label>
                <Input
                  type="textarea"
                  value={values.closeComment}
                  onChange={handleChange('closeComment')}
                />
              </FormGroup>
            </>
          )}

          {stateIdx >= 5 && (
            <>
              <FormGroup>
                <Label>Fecha pago (completado)</Label>
                <Input
                  type="date"
                  value={values.completedDate}
                  onChange={handleChange('completedDate')}
                />
              </FormGroup>
              <FormGroup>
                <Label>Precio (completado)</Label>
                <Input
                  type="text"
                  value={values.completedPrice ?? ''}
                  onChange={(e) => {
                    const raw = (e && e.target != null) ? e.target.value : '';
                    setValues((p) => ({ ...p, completedPrice: formatFormCurrency(raw) }));
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Método de pago (completado)</Label>
                <Input
                  type="select"
                  value={values.completedPaymentMethod}
                  onChange={handleChange('completedPaymentMethod')}
                >
                  {PAYMENT_METHOD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Comentario (completado)</Label>
                <Input
                  type="textarea"
                  value={values.completedComment}
                  onChange={handleChange('completedComment')}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="submit" color="primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          <Button type="button" color="secondary" onClick={() => toggle('cancel')}>
            Cancelar
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default LeadEditForm;
